"use client";
import type { BarChartProps } from "@mui/x-charts/BarChart";
import { BarChart } from "@mui/x-charts/BarChart";

import { useGetDeliveryStatsByTypeQuery } from "@/libs/generated/hooks";
import { DeliveryType } from "@/types/models/delivery.types";

const deliveryTypeColors: Partial<Record<DeliveryType, string>> = {
  [DeliveryType.STANDARD]: "#1a5da6",
  [DeliveryType.TO_RETURN]: "#d4a81b",
  [DeliveryType.TWO_WAY_DELIVERY]: "#8a4fce",
  [DeliveryType.OKM]: "#3da58a",
};

function getCustomDeliveryTypeColor(type: DeliveryType) {
  return deliveryTypeColors[type] || "#888888";
}

const valueFormatter = (value: number | null) => `${value ?? 0}`;

function DeliveryStatsByType() {
  const { data, isPending, error } = useGetDeliveryStatsByTypeQuery();

  if (isPending) return <div>Loading...</div>;
  if (error || !data) {
    return <div>Error loading delivery stats</div>;
  }

  const fetchedData = data.getDeliveryStatsByType;

  const dataset = fetchedData.map((item) => ({
    month: item.x,
    okm: item.y[DeliveryType.OKM],
    to_return: item.y[DeliveryType.TO_RETURN],
    quality_check: item.y[DeliveryType.TWO_WAY_DELIVERY],
    standard: item.y[DeliveryType.STANDARD],
  }));

  const maxY = Math.max(
    ...dataset.flatMap((d) =>
      [d.okm, d.to_return, d.quality_check, d.standard].filter((v) => typeof v === "number")
    )
  );

  const chartSetting = {
    yAxis: [
      {
        label: "",
        width: 60,
        scaleType: "linear",
        max: maxY * 1.3,
      },
    ],
    height: 500,
    margin: { bottom: 50 }, // Reduced bottom margin to bring labels closer
    sx: {
      "& .MuiChartsAxis-tickLabel": {
        fontWeight: 500,
      },
    },
  } satisfies Pick<BarChartProps, "yAxis" | "height" | "margin" | "sx">;

  return (
    <BarChart
      dataset={dataset}
      xAxis={[
        {
          scaleType: "band",
          dataKey: "month",
          tickPlacement: "middle", // Align ticks in the middle of the bars
          tickLabelPlacement: "middle", // Ensure labels align with ticks
          tickLabelStyle: {
            fontSize: 12,
          },
        },
      ]}
      series={[
        {
          dataKey: "standard",
          label: "STANDARD",
          valueFormatter,
          color: getCustomDeliveryTypeColor(DeliveryType.STANDARD),
        },
        {
          dataKey: "to_return",
          label: "TO_RETURN",
          valueFormatter,
          color: getCustomDeliveryTypeColor(DeliveryType.TO_RETURN),
        },
        {
          dataKey: "quality_check",
          label: "TWO_WAY_DELIVERY",
          valueFormatter,
          color: getCustomDeliveryTypeColor(DeliveryType.TWO_WAY_DELIVERY),
        },
        {
          dataKey: "okm",
          label: "OKM",
          valueFormatter,
          color: getCustomDeliveryTypeColor(DeliveryType.OKM),
        },
      ]}
      {...chartSetting}
    />
  );
}

export default DeliveryStatsByType;
